
import { allLogicRules } from '../logic-rules';
import type { LogicRule } from '../logic-rules';
import type { RuleData } from '../types/puzzle-types';

/**
 * Finds a rule by name, accounting for spectrum values if provided
 */
export function findRuleByName(ruleName: string, spectrum?: [string, string]): LogicRule | null {
  console.log('Finding rule EXACTLY as provided in spreadsheet:', ruleName);
  
  // Create a new rule directly from the input parameters
  const baseRuleName = ruleName.split(/\s+to\s+/i)[0].trim();
  // Use the exact description from spectrum without reversing or changing anything
  const description = spectrum ? `${spectrum[0]} • ${spectrum[1]}` : '';
  
  // Generate a unique ID
  const id = `spreadsheet_${baseRuleName.toLowerCase().replace(/\s+/g, '_')}`;
  
  // Create a placeholder sort function that does NOT reorder words - we'll use spreadsheet order exactly
  const sortFn = (a: string, b: string) => 0;
  
  console.log('Creating rule with EXACT spreadsheet values:', {
    id,
    name: ruleName,
    description,
    spectrum
  });
  
  // Create and return the rule, maintaining the exact order and name from spreadsheet
  return {
    id,
    name: ruleName, // Use full name including "to" part
    description,
    sortFn,
    category: "spreadsheet" as any
  };
}

/**
 * Prepares a rule for storage by simplifying it to just the necessary fields
 */
export function prepareRuleForStorage(rule: LogicRule): RuleData {
  return {
    id: rule.id,
    name: rule.name,
    description: rule.description,
    reasoning: rule.reasoning
  };
}

/**
 * Extracts a corrected order from the reasoning text
 * Returns the corrected order if possible, or null if not
 */
export function extractOrderFromReasoning(words: string[], reasoning: string): string[] | null {
  if (!reasoning) return null;
  
  console.log('Attempting to extract order from reasoning:', reasoning);
  console.log('Available words:', words);
  
  // Convert everything to lowercase for comparison
  const lowerReasoning = reasoning.toLowerCase();
  const lowerWords = words.map(w => w.toLowerCase());
  
  // Create a map to store each word's first position in the reasoning
  const wordPositions: Record<string, number> = {};
  
  // Find the first position of each word in the reasoning
  for (const word of lowerWords) {
    const position = lowerReasoning.indexOf(word);
    if (position !== -1) {
      wordPositions[word] = position;
    } else {
      console.warn(`Word "${word}" not found in reasoning`);
      return null; // If any word is missing, we can't reliably extract the order
    }
  }
  
  // Sort words based on their position in the reasoning
  const sortedLowerWords = [...lowerWords].sort((a, b) => {
    return wordPositions[a] - wordPositions[b];
  });
  
  // Map back to original case words
  const sortedWords = sortedLowerWords.map(lowerWord => {
    const index = lowerWords.indexOf(lowerWord);
    return words[index];
  });
  
  console.log('Extracted order from reasoning:', sortedWords);
  return sortedWords;
}

/**
 * Verifies that the ordering of the words matches the reasoning
 * If inconsistent, attempts to correct the order based on reasoning
 * Returns { consistent: boolean, correctedOrder: string[] | null }
 */
export function verifyOrderMatchesReasoning(words: string[], correctOrder: string[], reasoning: string): { 
  consistent: boolean, 
  correctedOrder: string[] | null 
} {
  if (!reasoning) return { consistent: true, correctedOrder: null }; // If no reasoning, assume it's correct
  
  // Check if all words appear in the reasoning
  const allWordsInReasoning = words.every(word => 
    reasoning.toLowerCase().includes(word.toLowerCase())
  );
  
  if (!allWordsInReasoning) {
    console.warn('Not all words appear in the reasoning, skipping verification');
    return { consistent: true, correctedOrder: null }; // Skip verification if not all words appear in reasoning
  }
  
  // Try to extract the correct order from reasoning
  const extractedOrder = extractOrderFromReasoning(words, reasoning);
  
  if (!extractedOrder) {
    console.warn('Could not extract order from reasoning');
    return { consistent: true, correctedOrder: null };
  }
  
  // Check if the current order matches the extracted order
  const isConsistent = correctOrder.every((word, index) => 
    word.toLowerCase() === extractedOrder[index].toLowerCase()
  );
  
  if (!isConsistent) {
    console.warn('Order inconsistency detected between current order and reasoning!');
    console.log('Current order:', correctOrder);
    console.log('Order extracted from reasoning:', extractedOrder);
    
    return { 
      consistent: false, 
      correctedOrder: extractedOrder 
    };
  }
  
  return { consistent: true, correctedOrder: null };
}
